"use client";

/**
 * RoleBadge.tsx
 * REQ-028: User Profile UI - Badge showing role/tier with appropriate styling
 *
 * Displays user role as a colored chip, with colors varying by tier level
 * and portal-specific styling for Humanda marketplace roles.
 */

import * as React from "react";
import Chip, { type ChipProps } from "@mui/material/Chip";
import type { SxProps, Theme } from "@mui/material/styles";
import type { UserType } from "./types";

export interface RoleBadgeProps {
  /** User's role/type */
  role: UserType;
  /** Tier level display (T1-T5) */
  tier: string;
  /** Current portal context */
  portal?: "apal" | "humanda" | null;
  /** Chip size */
  size?: "small" | "medium";
  /** Additional MUI sx props */
  sx?: SxProps<Theme>;
  /** Test ID for E2E testing */
  "data-testid"?: string;
}

/**
 * Role display names mapping
 */
const roleDisplayNames: Record<UserType, string> = {
  standard: "Standard User",
  employee: "Employee",
  team_lead: "Team Lead",
  vendor: "Vendor",
  manager: "Manager",
  executive: "Executive",
  admin: "Administrator",
  buyer: "Buyer",
  broker: "Broker",
  seller: "Seller",
};

/**
 * Get chip color based on tier level (APAL context)
 * T5 = Admin (error/red)
 * T4 = Executive (warning/orange)
 * T3 = Manager (primary/blue)
 * T2 = Employee/Vendor (info/light blue)
 * T1 = Standard (default/grey)
 */
function getTierColor(tier: string): ChipProps["color"] {
  switch (tier) {
    case "T5": {
      return "error"; // Admin - Red
    }
    case "T4": {
      return "warning"; // Executive - Orange
    }
    case "T3": {
      return "primary"; // Manager - Blue
    }
    case "T2": {
      return "info"; // Employee/Vendor - Light Blue
    }
    default: {
      // T1 and unknown tiers - Standard (Grey)
      return "default";
    }
  }
}

/**
 * Get chip color for Humanda marketplace roles
 */
function getHumandaRoleColor(role: UserType): ChipProps["color"] | null {
  switch (role) {
    case "buyer": {
      return "info"; // Buyers - Info blue
    }
    case "broker": {
      return "secondary"; // Brokers - Secondary purple
    }
    case "seller": {
      return "success"; // Sellers - Success green
    }
    default: {
      return null; // Fall back to tier-based color
    }
  }
}

/**
 * RoleBadge Component
 *
 * Displays the user's role with tier-appropriate coloring.
 * Humanda portal shows marketplace-specific colors for buyer/broker/seller.
 *
 * @example
 * ```tsx
 * // APAL context - tier-based coloring
 * <RoleBadge role="manager" tier="T3" portal="apal" />
 *
 * // Humanda context - marketplace coloring for broker
 * <RoleBadge role="broker" tier="T4" portal="humanda" />
 * ```
 */
export function RoleBadge({
  role,
  tier,
  portal = null,
  size = "small",
  sx,
  "data-testid": testId = "role-badge",
}: RoleBadgeProps): React.JSX.Element {
  // Determine chip color
  let chipColor: ChipProps["color"];

  // For Humanda portal, check for marketplace-specific roles first
  if (portal === "humanda") {
    const humandaColor = getHumandaRoleColor(role);
    chipColor = humandaColor ?? getTierColor(tier);
  } else {
    // APAL or unknown portal uses tier-based colors
    chipColor = getTierColor(tier);
  }

  // Get display name for the role
  const displayName = roleDisplayNames[role] || role;

  return (
    <Chip
      label={displayName}
      color={chipColor}
      size={size}
      sx={{
        height: size === "small" ? 20 : 24,
        fontSize: size === "small" ? "0.75rem" : "0.8125rem",
        fontWeight: 500,
        ...sx,
      }}
      data-testid={testId}
    />
  );
}
